import { autocomplete } from "@algolia/autocomplete-js";
import "@algolia/autocomplete-theme-classic";
import { meilisearchAutocompleteClient } from "@meilisearch/autocomplete-client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Dropdown,
  Form,
  InputGroup,
  Nav,
  Navbar,
  Placeholder,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent, validateUserPermission } from "../../services/PermissionGuard";
import ShortcutService from "../../services/ShortcutService";
import translate from "../../services/Translate";
import languages from "../../services/languages";
import DstButton from "../DstButton/DstButton";
import Popup from "../Popup/Popup";
import ShortcutsOffcanvas from "../ShortcutsOffcanvas/ShortcutsOffcanvas";
import { getMeilisearchSources, saveResultInHistory } from "./DstHeader.function";
import "./DstHeader.scss";

const dropdownItemClassName = "d-flex align-items-center gap-2 rounded-2 px-2 py-1 xs-regular";

const DstHeader = ({ language }: { language: string }) => {
  const { first_name, handleLogout, loaded, user_id, theme, switchTheme, switchLanguage, permissions } = useSession();
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [pageName, setPageName] = useState("");
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const stopListening = ShortcutService.listen("?", () => setShowShortcuts((previous) => !previous), false, null);
    return () => stopListening();
  }, []);

  const _openModal = () => {
    setPageName(document.title);
    setShowModal(true);
  };

  const _saveUrl = () => {
    const currentUrl = window.location.pathname + window.location.search;
    const savedUrls: { name: string; url: string }[] = JSON.parse(localStorage.getItem("nexus-saved-urls") || "[]");

    const newEntry = { name: pageName.trim(), url: currentUrl };

    const existingIndex = savedUrls.findIndex((entry) => entry.url === currentUrl);
    if (existingIndex !== -1) {
      savedUrls[existingIndex] = newEntry;
    } else {
      savedUrls.push(newEntry);
    }

    localStorage.setItem("nexus-saved-urls", JSON.stringify(savedUrls));
    setShowModal(false);
    setShowToast(true);
  };

  const navLinks = useMemo(
    () => [
      {
        permission: [{ methods: ["GET"], route: "/roles" }],
        target: "/permissions",
        icon: "dst-icon-shield-03",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.ADMIN"),
      },
      {
        target: "/users",
        icon: "dst-icon-folder-lock",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.TRAIN"),
      },
      {
        permission: [{ methods: ["GET"], route: "/insights/users" }],
        target: "/insights",
        icon: "dst-icon-data",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.INSIGHTS"),
      },
      {
        permission: [{ methods: ["GET"], route: "/evaluations/attempts" }],
        target: "/evaluations/attempts?status=CORRECTION_PENDING,PRE_CORRECTED",
        icon: "dst-icon-graduation-hat-02",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.ATTEMPTS"),
      },
      {
        permission: [{ methods: ["GET"], route: "/cursus" }],
        target: "/invitations",
        icon: "dst-icon-image-user-plus",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.INVITATION"),
      },
      {
        permission: [{ methods: ["GET"], route: "/ephemeral-evaluations" }],
        target: "/ephemeral/sessions",
        icon: "dst-icon-file-question-02",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.EPHEMERAL"),
      },
      {
        permission: [{ methods: ["GET"], route: "/meetings" }],
        target: "/meetings",
        icon: "dst-icon-clock",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.MEETING"),
      },
      {
        permission: [{ methods: ["GET"], route: "/documents/diplomas" }],
        target: "/diplomas",
        icon: "dst-icon-paperclip",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.DIPLOMAS"),
      },
      {
        permission: [{ methods: ["GET"], route: "/projects" }],
        target: "/projects",
        icon: "dst-icon-globe-02",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.PROJECT"),
      },
      {
        permission: [{ methods: ["GET"], route: "/messages" }],
        target: "/messages",
        icon: "dst-icon-mail-02",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.MESSAGES"),
      },
      {
        permission: [{ methods: ["GET"], route: "/demos/records" }],
        target: "/demo/records",
        icon: "dst-icon-database-01",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.DEV"),
      },
      {
        permission: [{ methods: ["GET"], route: "/landing-page/job-blocks" }],
        target: "/landing-page/job-blocks",
        icon: "dst-icon-code-browser",
        label: translate(language, "COMPONENTS.HEADER.NAVIGATION.LANDING_PAGE"),
      },
    ],
    [language]
  );

  useEffect(() => {
    if (!containerRef.current || !validateUserPermission([{ methods: ["GET"], route: "/search" }], permissions)) return;

    const searchClient = meilisearchAutocompleteClient({
      url: process.env.REACT_APP_MEILISEARCH_URL!,
      apiKey: process.env.REACT_APP_MEILISEARCH_API_KEY,
    });

    const shortcutDestructor = ShortcutService.listen(
      "k",
      () => containerRef.current?.getElementsByTagName("input")?.[0].focus(),
      true
    );

    const searchCore = autocomplete({
      container: containerRef.current!,
      placeholder: translate(language, "COMPONENTS.HEADER.SEARCH_PLACEHOLDER").replace(
        "{{control}}",
        (navigator?.platform || (navigator as any)?.userAgentData?.platform).toUpperCase().includes("MAC")
          ? "⌘"
          : "Ctrl"
      ),
      defaultActiveItemId: 0,
      getSources: ({ query }: { query: string }) => getMeilisearchSources(searchClient, query, !query),
      openOnFocus: true,
      navigator: {
        navigate({ itemUrl, item }) {
          saveResultInHistory(itemUrl, item);
          window.location.assign(itemUrl);
        },
        navigateNewTab({ itemUrl, item }) {
          saveResultInHistory(itemUrl, item);
          window.open(itemUrl, "_blank", "noopener")?.focus();
        },
        navigateNewWindow({ itemUrl, item }) {
          saveResultInHistory(itemUrl, item);
          window.open(itemUrl, "_blank", "noopener");
        },
      },
    });

    return () => {
      shortcutDestructor();
      searchCore.destroy();
    };
  }, [language, permissions]);

  return (
    <React.Fragment>
      <Navbar expand="md" id="navbar">
        <Container fluid>
          <Navbar.Brand as={Link} to="/">
            <img src="/assets/images/nexus.svg" alt="DataScientest" height="30px" className="mb-1" />
          </Navbar.Brand>
          <Navbar.Collapse id="navbar-nav" className="order-2 order-md-1">
            <Nav className="align-items-center xs-bold w-100 flex-row flex-wrap justify-content-center justify-content-md-start gap-2 gap-xxl-3 mt-3 mt-md-0">
              {navLinks.map(({ permission, target, icon, label }) =>
                permission ? (
                  <PermissionComponent key={target} neededPermission={permission}>
                    <NavLink className="d-flex align-items-center text-decoration-none" to={target}>
                      <span className={`${icon} me-1`} />
                      {label}
                    </NavLink>
                  </PermissionComponent>
                ) : (
                  <NavLink key={target} className="d-flex align-items-center text-decoration-none" to={target}>
                    <span className={`${icon} me-1`} />
                    {label}
                  </NavLink>
                )
              )}
            </Nav>
            <Nav className="my-2 my-md-0 me-md-2">
              <InputGroup>
                <div id="searcher-container" ref={containerRef} />
              </InputGroup>
            </Nav>
          </Navbar.Collapse>
          <div className="d-flex align-items-center gap-2 ms-auto order-1 order-md-2">
            <Navbar.Toggle aria-controls="navbar-nav" className="p-1" />
            <Dropdown align="end" id="dst-header-menu">
              <Dropdown.Toggle
                id="nexus-menu-toggle"
                variant="outline-primary"
                size="sm"
                className="p-1 d-flex align-items-center justify-content-center"
                title={translate(language, "COMPONENTS.HEADER.MENU.TITLE")}
              >
                <span className="dst-icon-settings-01 h3 fw-normal mb-0" aria-hidden="true" />
              </Dropdown.Toggle>
              <Dropdown.Menu className="p-2 shadow-sm rounded-3 overflow-y-auto">
                <Dropdown.Item
                  as="button"
                  className={`${dropdownItemClassName} overflow-hidden`}
                  disabled={!loaded}
                  onClick={() => navigate(`/user/${user_id}`)}
                >
                  <span className="dst-icon-image-user" aria-hidden="true" />
                  <span>{translate(language, "COMPONENTS.HEADER.MENU.MY_ACCOUNT")}</span>
                  {loaded ? (
                    <span className="ms-auto text-body-secondary text-truncate">{first_name}</span>
                  ) : (
                    <Placeholder as="span" animation="glow" className="ms-auto w-25">
                      <Placeholder xs={12} />
                    </Placeholder>
                  )}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Header className="px-2 xxs-bold text-uppercase">
                  {translate(language, "COMPONENTS.HEADER.MENU.TOOLS")}
                </Dropdown.Header>
                <Dropdown.Item as="button" className={dropdownItemClassName} onClick={() => setShowShortcuts(true)}>
                  <span className="dst-icon-help-hexagon" aria-hidden="true" />
                  <span>{translate(language, "COMPONENTS.HEADER.MENU.SHORTCUTS")}</span>
                  <kbd className="ms-auto bg-body-tertiary black-font border xxs-regular text-nowrap lh-1">?</kbd>
                </Dropdown.Item>
                <Dropdown.Item as="button" className={dropdownItemClassName} onClick={_openModal}>
                  <span className="dst-icon-bookmark" aria-hidden="true" />
                  {translate(language, "COMPONENTS.HEADER.MENU.ADD_FAVORITES")}
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Header className="px-2 xxs-bold text-uppercase">
                  {translate(language, "COMPONENTS.HEADER.MENU.PREFERENCES")}
                </Dropdown.Header>
                <div className="d-flex align-items-center gap-2 px-2 py-1 xs-regular">
                  <span className="dst-icon-translate-01" aria-hidden="true" />
                  <Form.Label htmlFor="nexus-menu-language" className="mb-0">
                    {translate(language, "COMPONENTS.HEADER.MENU.LANGUAGE")}
                  </Form.Label>
                  <Form.Select
                    id="nexus-menu-language"
                    size="sm"
                    className="ms-auto w-auto"
                    value={language}
                    onChange={(event) => switchLanguage(event.target.value)}
                  >
                    {Object.entries(languages).map(([code, locale]) => (
                      <option key={code} value={code}>
                        {locale.LANGUAGE}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2 px-2 py-1 xs-regular">
                  <span className="dst-icon-moon-01" aria-hidden="true" />
                  <Form.Label htmlFor="nexus-menu-theme" className="mb-0">
                    {translate(language, "COMPONENTS.HEADER.MENU.DARK_MODE")}
                  </Form.Label>
                  <Form.Check
                    id="nexus-menu-theme"
                    type="switch"
                    className="ms-auto"
                    aria-label={translate(language, "COMPONENTS.HEADER.MENU.DARK_MODE")}
                    checked={theme === "dark"}
                    onChange={() => switchTheme(theme === "light" ? "dark" : "light")}
                  />
                </div>
                <Dropdown.Divider />
                <Dropdown.Item as="button" className={dropdownItemClassName} onClick={() => handleLogout()}>
                  <span className="dst-icon-log-out-01" aria-hidden="true" />
                  {translate(language, "COMPONENTS.HEADER.MENU.LOGOUT")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Container>
        <Popup
          show={showModal}
          onClose={() => setShowModal(false)}
          modalTitle={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.TITLE")}
          footerContent={
            <React.Fragment>
              <DstButton
                value={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.BUTTON_CANCEL")}
                variant="secondary"
                clickFunction={() => setShowModal(false)}
              />
              <DstButton
                value={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.BUTTON_CONFIRM")}
                clickFunction={_saveUrl}
                disabled={!pageName.trim()}
              />
            </React.Fragment>
          }
        >
          <Form onSubmit={(event) => event.preventDefault()}>
            <Form.Group>
              <Form.Label>{translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.LABEL")}</Form.Label>
              <Form.Control
                type="text"
                value={pageName}
                onChange={(event) => setPageName(event.target.value)}
                placeholder={translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.MODAL.PLACEHOLDER")}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Popup>
        <ToastContainer position="bottom-end" className="p-3">
          <Toast bg="success" show={showToast} onClose={() => setShowToast(false)} delay={1500} autohide>
            <Toast.Body className="text-white">
              {translate(language, "COMPONENTS.HEADER.ADD_FAVORITES.SUCCESS_SAVE")}
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </Navbar>
      <ShortcutsOffcanvas show={showShortcuts} onHide={() => setShowShortcuts(false)} language={language} />
    </React.Fragment>
  );
};

export default DstHeader;
